/* eslint-disable react/forbid-dom-props */
import Image from 'next/future/image';
import React, { useRef, useState, Fragment, useEffect } from 'react';
import { Breakpoints } from 'settings/breakpoints';
import { Dialog, Transition } from '@headlessui/react';
import Slider from '../slider/Slider';
import settings from '@/settings';
import { X } from '@/public/icons/account/icons';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import playIcon from '@/public/icons/homepage/circular-economy/play.svg';

const breakpoints = {
  [Breakpoints.NONE]: {
    slidesPerView: 1.5,
    spaceBetween: 15,
  },
  [Breakpoints.XS]: {
    slidesPerView: 2.5,
    spaceBetween: 15,
  },
  [Breakpoints.MD]: {
    slidesPerView: 3.5,
    spaceBetween: 15,
  },
  [Breakpoints.LG]: {
    slidesPerView: 5,
    spaceBetween: 15,
  },
};

type TestimonialT = { title: string; path: string; thumbnail: string };

type TestimonialCardProps = {
  title: string;
  path: string;
  thumbnail: string;
  setSelected: ({
    title,
    path,
  }: {
    title: string;
    path: string;
    thumbnail: string;
  }) => void;
};

const isAUStore = settings.store === 'reebelo-au';

const generateStoreExlusiveData = (handle: string) => {
  if (isAUStore) {
    return {
      path: getEdgeUrl(`videos/circular-economy/${handle}_au.mp4`),
      thumbnail: getEdgeUrl(`images/circular-economy/${handle}_au.png`),
    };
  }

  return {
    path: getEdgeUrl(`videos/circular-economy/${handle}.mp4`),
    thumbnail: getEdgeUrl(`images/circular-economy/${handle}.png`),
  };
};

const TESTIMONIALS = [
  {
    title:
      "Moms, take notes. If you're not on refurbished, what are you doing?",
    path: getEdgeUrl('videos/circular-economy/testimonial_8.mp4'),
    thumbnail: getEdgeUrl('images/circular-economy/testimonial_8.png'),
  },
  {
    title:
      'Cayman Howell and her authentic mom content are goals! And we are thrilled that you are loving your refurnished apple watch 🎀⌚️',
    path: getEdgeUrl('videos/circular-economy/testimonial_9.mp4'),
    thumbnail: getEdgeUrl('images/circular-economy/testimonial_9.png'),
  },
  {
    title: 'An iPhone 12 Pro Max for $460???! #refurbished #reebelo #iphone',
    path: getEdgeUrl('videos/circular-economy/testimonial_10.mp4'),
    thumbnail: getEdgeUrl('images/circular-economy/testimonial_10.png'),
  },
  {
    title:
      "You Won't Believe How EASY it is to Upgrade Your Apple Watch #techhacks #applewatch #refurbishedtech",
    path: getEdgeUrl('videos/circular-economy/testimonial_4.mp4'),
    thumbnail: getEdgeUrl('images/circular-economy/testimonial_4.png'),
  },
  {
    title: isAUStore
      ? 'Is a refurbished iPhone from Reebelo worth it? #iphonerefurbished #reebelo #techhacks #moneyhacks '
      : 'I Never Knew How Much I Needed an iPad Until I Got One ',
    ...generateStoreExlusiveData('testimonial_5'),
  },
];

function VideoModal({
  closeModal,
  selected,
  isOpen,
}: {
  closeModal: () => void;
  selected: TestimonialT | null;
  isOpen: boolean;
}) {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-700/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative h-screen w-screen overflow-hidden bg-white p-5 text-left shadow-xl transition-all sm:h-auto sm:w-screen sm:max-w-[600px] sm:rounded-lg">
                <div className="flex h-full flex-col">
                  <Dialog.Title
                    as="div"
                    className="flex items-center justify-between gap-3"
                  >
                    <h3 className="grow-0 truncate text-sm leading-6 text-gray-700/50">
                      {selected?.title}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="shrink-0 text-gray-700/50 transition duration-150 ease-in-out hover:scale-110 hover:text-gray-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Title>
                  <div className="mt-5 flex-1">
                    <video
                      controls
                      className="mx-auto block h-full min-h-[75vh] w-full object-contain object-top"
                      autoPlay
                      style={{ aspectRatio: '16/9' }}
                    >
                      <source src={selected?.path} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

const TestimonialCard = (props: TestimonialCardProps) => {
  const { path, title, thumbnail } = props;
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    if (videoRef.current) (videoRef.current as HTMLVideoElement).play();
  };

  const handleMouseLeave = () => {
    if (videoRef.current) (videoRef.current as HTMLVideoElement).pause();
  };

  return (
    <button
      onClick={() => props.setSelected({ title, path, thumbnail })}
      className="group relative h-full w-full grow-0 cursor-pointer outline-none"
      aria-label={`Video: ${title}`}
    >
      <div className="absolute top-1/2 left-1/2 flex h-12 w-12 -translate-y-1/2 -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-lg group-hover:hidden sm:h-14 sm:w-14">
        <Image
          src={playIcon}
          alt="Play Video"
          width={100}
          height={100}
          className="h-4 w-4 sm:h-5 sm:w-5"
        />
      </div>
      {/* We cant't play the video on hover on mobile + causing the video to play twice at the same time on click */}
      {window?.navigator?.userAgent?.includes('Mobile') ? (
        <Image
          src={thumbnail}
          alt={`Testimonial Video`}
          height={300}
          width={100}
          className="h-full max-h-[50vh] w-full rounded-xl object-cover"
          quality={100}
        />
      ) : (
        <video
          className="h-full max-h-[50vh] w-full rounded-xl object-cover"
          ref={videoRef}
          // Play Video on Mouse Enter
          onMouseEnter={handleMouseEnter}
          // Pause Video on Mouse Leave
          onMouseLeave={handleMouseLeave}
          poster={thumbnail}
          muted
          disablePictureInPicture
          disableRemotePlayback
          preload="none"
        >
          <source src={path} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </button>
  );
};

export default function CircularEconomy() {
  const [selectedVideo, setSelectedVideo] = useState<TestimonialT | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (selectedVideo != null) setIsOpen(true);
  }, [selectedVideo]);

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSelectedVideo(null);
    }, 200);
  };

  return (
    <>
      <VideoModal
        isOpen={isOpen}
        closeModal={closeModal}
        selected={selectedVideo}
      />
      <div>
        <h2 className="flex items-center text-lg font-semibold md:text-xl">
          Join the Circular Economy
        </h2>
        <p className="mt-4 text-sm">
          Fuel your passions with good tech. See how people around the world are
          giving a new life to refurbished tech with Reebelo.
        </p>

        <div className="relative mt-5 gap-3">
          <Slider breakpoints={breakpoints}>
            {TESTIMONIALS.map(({ title, path, thumbnail }, i) => (
              <TestimonialCard
                key={`${i}-${title}`}
                title={title}
                path={path}
                thumbnail={thumbnail}
                setSelected={setSelectedVideo}
              />
            ))}
          </Slider>
        </div>
      </div>
    </>
  );
}
